import type { ActivitySection } from '@strava/mre';
import { RoadSurfaceType } from '@strava/mre';

import type { LatLng } from '../types';

/**
 * Accepts a startIndex (end of start privacy zone), endIndex (start of end privacy zone) + polyline
 * Returns an array of ActivitySection objects.
 *
 * ActivitySection objects have a particular surface type and a privateSection attribute.
 *
 * The sum of ActivitySection.numPolylineEdges values across all sections has to be
 * equal to the number of vertices of the line minus 1.
 */

export const getPrivacyZonesFromIndices = (
  startIndex = 0,
  endIndex = 0,
  polyline: LatLng[] = []
): ActivitySection[] => {
  // Polyline must have at least 2 points to form a valid section
  if (polyline.length < 2) {
    return [];
  }

  const publicSectionLength = polyline.length - endIndex - startIndex - 1; // -1 to account for the number of edges being one less than number of points

  // If there is no public section, return a single private section covering the entire polyline length - 1
  if (publicSectionLength < 1) {
    return [
      {
        numPolylineEdges: BigInt(polyline.length - 1),
        privateSection: true,
        surfaceType: RoadSurfaceType.PAVED
      }
    ];
  }

  return [
    // Add a private section at the start if startIndex is greater than 0
    ...(startIndex > 0
      ? [
          {
            numPolylineEdges: BigInt(startIndex),
            privateSection: true,
            surfaceType: RoadSurfaceType.PAVED
          }
        ]
      : []),
    // Add a public section in the middle if publicSectionLength is greater than 0
    ...(publicSectionLength > 0
      ? [
          {
            numPolylineEdges: BigInt(publicSectionLength),
            privateSection: false,
            surfaceType: RoadSurfaceType.PAVED
          }
        ]
      : []),
    // Add a private section at the end if endIndex is greater than 0
    ...(endIndex > 0
      ? [
          {
            numPolylineEdges: BigInt(endIndex),
            privateSection: true,
            surfaceType: RoadSurfaceType.PAVED
          }
        ]
      : [])
  ];
};
